import type { TVariableProps } from "@/dialogs/share-dialog/types";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { UseFormReturn } from "react-hook-form";

/**
 * The org variable DEFINITIONS a repeatable row's fields point at, keyed by
 * variable name (= the row field key). A row field only stores key/name/type;
 * the select options, AI prompt and default value live on the org variable,
 * so the cells need this lookup to render the right control and seed rows.
 *
 * `form` is the version form: the AI cell reads top-level values from it as
 * prompt inputs. Both are optional — a card rendered without a provider (tests)
 * falls back to plain text cells and empty defaults.
 */
export interface RepeatableDefinitions {
  definitions: Map<string, TVariableProps>;
  form?: UseFormReturn<Record<string, unknown>>;
}

const EMPTY: RepeatableDefinitions = { definitions: new Map() };

const RepeatableDefinitionsContext =
  createContext<RepeatableDefinitions>(EMPTY);

export const RepeatableDefinitionsProvider = ({
  definitions,
  form,
  children,
}: RepeatableDefinitions & { children: ReactNode }) => {
  const value = useMemo(() => ({ definitions, form }), [definitions, form]);
  return (
    <RepeatableDefinitionsContext.Provider value={value}>
      {children}
    </RepeatableDefinitionsContext.Provider>
  );
};

export const useRepeatableDefinitions = (): RepeatableDefinitions =>
  useContext(RepeatableDefinitionsContext);

/** Index variable definitions by name. */
export const toDefinitionMap = (
  variables: TVariableProps[],
): Map<string, TVariableProps> => {
  const map = new Map<string, TVariableProps>();
  for (const variable of variables) {
    if (variable?.name && !map.has(variable.name))
      map.set(variable.name, variable);
  }
  return map;
};
